import { ComponentProps, FC, useMemo } from 'react';

import { IoEllipsisVertical } from 'react-icons/io5';
import { Checkbox } from 'ui/components/form';
import { NavAction } from 'ui/components/nav';
import { NOTIFICATION_STATUSES } from 'utils/constants';
import { cn, useAdmin } from 'utils/helpers';
import { Notification } from 'utils/types';
import { dayjs } from 'utils/providers/Timezone';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { useUpdateAdminNotificationStatus } from 'modules/admin/notifications/hooks';

import { useUpdateNotificationStatus } from '../hooks';
import NotificationIcon from './NotificationIcon';
import NotificationLog from './notiifcations/NotificationLog';

type PROPS = {
  data: Notification;
  onSelect: (data: PROPS['data']) => void;
  isChecked: boolean;
};

const NotificationItem: FC<PROPS> = ({ data, onSelect, isChecked }) => {
  const { isAdmin } = useAdmin();
  const {
    state: { isPreview },
  } = useAppWrapperContext();

  const { READ, UNREAD } = NOTIFICATION_STATUSES;
  const relativeTimeFromToday = dayjs().to(dayjs(data.createdAt));
  const { mutate: updateNotificationStatus } = (
    isAdmin && !isPreview
      ? useUpdateAdminNotificationStatus
      : useUpdateNotificationStatus
  )();

  const actions: ComponentProps<typeof NavAction>['options'] = [
    {
      key: '1',
      text: 'Mark as Unread',
      onClick: () => {
        updateNotificationStatus({ id: data._id, status: UNREAD });
      },
    },
    {
      key: '2',
      text: 'Mark as Read',
      onClick: () => {
        updateNotificationStatus({ id: data._id, status: READ });
      },
    },
  ];

  const notificationDetails = useMemo(() => {
    const notification = NotificationLog?.[data.source]?.[data.action];

    return {
      title: notification?.title ?? '',
      source: data?.source,
      element: () => notification?.element(data),
    };
  }, [data]);

  return (
    <div
      className={cn(
        data?.status === UNREAD && 'bg-fill-primary text-secondary-base',
        data?.status === READ && 'bg-fill-tertiary',
        isChecked && 'bg-fill-primary',
        'transition',
        'hover:bg-fill-secondary',
        'text-content px-[14px] py-[14px]',
        'group',
      )}
    >
      <div
        className={cn(
          'flex justify-between items-center',
          'text-xs font-normal leading-[16px]',
        )}
      >
        <div className="flex items-center w-full gap-3">
          <Checkbox
            defaultChecked={isChecked}
            onChange={() => onSelect(data)}
          />
          <NotificationIcon source={notificationDetails?.source} />
          <p className="text-xs">{notificationDetails?.title}</p>
        </div>
        <div className="flex-shrink whitespace-nowrap flex items-center gap-[9px]">
          {relativeTimeFromToday}
          <NavAction
            label={<IoEllipsisVertical className="w-4 h-4" />}
            options={actions}
            className={cn(
              'border border-primary-base',
              'w-6 h-6 rounded-full',
              'justify-center items-center',
              'group-hover:flex hidden',
            )}
          />
        </div>
      </div>
      <div className="pl-[27px] py-3 space-y-[6px]">
        {notificationDetails?.element()}
      </div>
    </div>
  );
};

export default NotificationItem;
